import React, { useState, useEffect, useCallback } from "react";
import ReactFlow, {
  addEdge,
  useNodesState,
  useEdgesState,
  Background,
} from "reactflow";
import "./Evidencemap.css";
import "reactflow/dist/style.css";
import {
  generateCompanyNode,
  generateGroupingNode,
  generateCategoryNodes,
  generatePropertyNodes,
} from "./Nodes";

import { generateCategoryEdges, generatePropertyEdges } from "./Edges";
import {
  categoryPerPropertyNum,
  sortCategories,
  sortNodeAlternation,
} from "./FilterFunctions";
import CheckboxContainer from "./CheckboxContainer";

function Flow() {
  // Use useState to manage nodes and edges
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [categoryVisibility, setCategoryVisibility] = useState({}); // array to track the visibility of property nodes per category node
  const [checkAllCategories, setCheckAllCategories] = useState(false); // state to keep track of Select All Checkbox

  // reactflow link hidden
  const proOptions = { hideAttribution: true };

  const onConnect = useCallback(
    (params) => setEdges((prevEdges) => addEdge(params, prevEdges)),
    [setEdges]
  );

  useEffect(() => {
    // reset nodes, edges and category visibility before generating evidence map
    setNodes([]);
    setEdges([]);
    setCategoryVisibility({});

    const mindMapData = JSON.parse(localStorage.getItem("UpdatedDataArray"));
    const companyName = localStorage.getItem("companyName");

    if (companyName && mindMapData) {
      // Calculate the width and height of the screen
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      // Calculate the width and height of the company node
      const companyNodeWidth = 800;
      const companyNodeHeight = 395;

      // Calculate the x and y positions for the company node to center it
      const companyNodeX = (screenWidth - companyNodeWidth) / 2;
      const companyNodeY = (screenHeight - companyNodeHeight) / 2;

      // Create the main node for the company name
      const companyNode = generateCompanyNode(
        companyName,
        companyNodeX,
        companyNodeY
      );

      // Add the main node to the list of nodes
      setNodes((prevNodes) => [...prevNodes, companyNode]);

      const nodesCount = {};

      // Add the category name to the list with the number of associated properties
      categoryPerPropertyNum(mindMapData, nodesCount);

      // Sort categories in descending order
      const sortCategoriesbyNumProperties = sortCategories(nodesCount);

      // Get values from the sorted categories
      const categoryOrder = Object.values(sortCategoriesbyNumProperties);
      const numCategories = categoryOrder.length;
      const sortedCategories = [];

      // Add category names to list by alternating from highest to lowest in number of properties
      sortNodeAlternation(categoryOrder, numCategories, sortedCategories);

      // Calculate the positions of category nodes in a circular pattern around the company node
      let radius;
      const maxProperties = 5;

      sortedCategories.forEach((category, index) => {
        const angle = (index / numCategories) * 2 * Math.PI;

        // Determine the width of the radius based on the number of properties per category
        if (nodesCount[category] <= 2) {
          radius = 350;
        } else if (nodesCount[category] >= 3 && nodesCount[category] <= 4) {
          radius = 800;
        } else if (
          nodesCount[category] > 4 &&
          nodesCount[category] <= maxProperties
        ) {
          radius = 1000;
        } else if (
          nodesCount[category] > maxProperties &&
          nodesCount[category] <= 7
        ) {
          radius = 1300;
        } else if (nodesCount[category] >= 7 && nodesCount[category] <= 10) {
          radius = 1700;
        } else if (nodesCount[category] > 10 && nodesCount[category] <= 15) {
          radius = 2300;
        } else if (nodesCount[category] > 15 && nodesCount[category] <= 17) {
          radius = 3200;
        } else if (nodesCount[category] > 18) {
          radius = 4200;
        }

        let categoryX = companyNodeX + radius * Math.cos(angle);
        let categoryY = companyNodeY + radius * Math.sin(angle);

        const groupNode = generateGroupingNode(category, categoryX, categoryY);

        // Add group nodes to the list of nodes
        setNodes((prevNodes) => [...prevNodes, groupNode]);

        const categoryNode = generateCategoryNodes(
          category,
          category,
          categoryX,
          categoryY
        );

        // Add category nodes to the list of nodes
        setNodes((prevNodes) => [...prevNodes, categoryNode]);

        // Create edges from the main node to category nodes
        const edge = generateCategoryEdges(
          category,
          companyName,
          categoryNode.id
        );

        // Add edges to the list of edges
        setEdges((prevEdges) => [...prevEdges, edge]);

        // Calculate the positions of property nodes within each category in a circular pattern
        const categoryProperties = mindMapData.filter(
          (item) =>
            item["Category (per property)"] === category &&
            item["Property"].trim() !== ""
        );

        // Update the positions of other nodes
        const numProperties = categoryProperties.length;
        let propertyRadius;
        const offsetAngle = Math.PI / 3.5;

        categoryProperties.forEach((property, propertyIndex) => {
          const propertyAngle =
            (propertyIndex / numProperties) * 2 * Math.PI + offsetAngle;

          // Determine the width of the radius based on the number of properties per category
          if (nodesCount[category] <= 2) {
            propertyRadius = 150;
          } else if (nodesCount[category] >= 3 && nodesCount[category] <= 4) {
            propertyRadius = 180;
          } else if (nodesCount[category] > 4 && nodesCount[category] <= 5) {
            propertyRadius = 210;
          } else if (nodesCount[category] > 5 && nodesCount[category] <= 7) {
            propertyRadius = 265;
          } else if (nodesCount[category] > 7 && nodesCount[category] <= 10) {
            propertyRadius = 310;
          } else if (nodesCount[category] > 10 && nodesCount[category] <= 15) {
            propertyRadius = 370;
          } else if (nodesCount[category] > 15 && nodesCount[category] <= 17) {
            propertyRadius = 480;
          } else if (nodesCount[category] > 18) {
            propertyRadius = 620;
          }

          let propertyX = categoryX + propertyRadius * Math.cos(propertyAngle);
          let propertyY = categoryY + propertyRadius * Math.sin(propertyAngle);

          // Adjust the property nodes position based on the group node position
          propertyX -= groupNode.position.x;
          propertyY -= groupNode.position.y;

          const propertyName = property["Property"];
          const propertyValue = property["Value (per property)"];

          // Create the property label with different colors for name and value
          const propertyLabel = `${propertyName} <br><div style="color: black">${propertyValue}</div>`;

          // Conditionally calculate the width and height based on text length
          const minNodeWidth = 10;
          const maxNodeWidth = 275;
          const minNodeHeight = 10;

          // Calculate the width based on text length
          const propertyNodeWidth = Math.min(
            maxNodeWidth,
            Math.max(minNodeWidth, propertyLabel.length * 3.4)
          );

          // Calculate the height based on text length
          const lines = propertyLabel.split("<br>").length;
          const propertyNodeHeight = Math.max(minNodeHeight, lines * 45);

          const propertyNode = generatePropertyNodes(
            property["Property"],
            propertyX,
            propertyY,
            propertyNodeWidth,
            propertyNodeHeight,
            propertyLabel,
            categoryNode.id
          );

          // Add property node to the list of nodes
          setNodes((prevNodes) => [...prevNodes, propertyNode]);

          // Create an edge from the category node to the property node
          const propertyEdge = generatePropertyEdges(
            property["Property"],
            categoryNode.id,
            propertyNode.id
          );

          setEdges((prevEdges) => [...prevEdges, propertyEdge]);
        });
      });
    }

    if (!companyName) {
      return "No Company Name";
    }

    if (!mindMapData) {
      return "No Mindmap Data";
    }
  }, [setNodes, setEdges]);

  return (
    <div style={{ height: 580 }} className="evidenceMap">
      <CheckboxContainer
        checkAllCategories={checkAllCategories}
        setCheckAllCategories={setCheckAllCategories}
        nodes={nodes}
        categoryVisibility={categoryVisibility}
        setCategoryVisibility={setCategoryVisibility}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
      />
      <ReactFlow
        id="evidencemap"
        style={{
          boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
          borderRadius: "6px",
        }}
        className="mindmap-container flex-child"
        nodes={nodes.filter((node) => {
          // Node type of category node
          if (node.type === "default") {
            // Add category node id to array and display the node on screen
            return categoryVisibility[node.id];
          }
          // Node type of property node
          if (node.type === "output") {
            // Add property nodes id to array and display the nodes on screen
            return categoryVisibility[node.data.categoryId];
          }
          // Display company node by default
          return true;
        })}
        edges={edges.filter((edge) => {
          // Check if both source and target nodes of edge are visible (category checkbox is checked)
          if (
            categoryVisibility[edge.source] &&
            categoryVisibility[edge.target]
          ) {
            // Return false to hide the edges
            return false;
          }
          // Return true to display edges of source and target nodes
          return true;
        })}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        connectionLineType="straight"
        nodesDraggable={true}
        proOptions={proOptions}
        minZoom={0.2}
        maxZoom={4}
      >
        <Background color="#" />
      </ReactFlow>
    </div>
  );
}

export default Flow;
